import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { FC, useMemo, useState } from 'react';
import { usePatientProfile } from '../../../../../../../../hooks/use-patient-profile';
import {
  ContactApi,
  PatientPreferencesRequest,
  PatientPreferencesResponse
} from '../../../../../../../../services/api-client/contact-api';
import { PatientProfileHeader } from '../../../../header/header';
import { QuickNavMenu } from '../../../../header/quick-nav-menu/quick-nav-menu';
import { usePatientProfileWidget } from '../../../../hooks/use-patient-profile-widget';
import { WidgetRoutePath } from '../../../../navigation/route-config';
import { ViewContentWrapper } from '../../../shared/view-content/view-content.styles';
import AllPromotionalSubSection from '../../shared/all-promotional-sub-section/all-promotional-sub-section';
import { PreferencesViewWrapper } from '../../shared/preferences-view-wrapper/preferences-view-wrapper';
import AllCommunicationSection from '../../shared/sub-section/all-communication-section';
import CommunicationSubSection from '../../shared/sub-section/communication-sub-section';
import CommunicationSubSectionHeader from '../../shared/sub-section/communication-sub-section-header';

export const TextMessagePreferencesView: FC = () => {
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { showSuccess } = useNotification();
  const { goTo } = usePatientProfileWidget();
  const { patientPreferences, refreshPatientPreferences } = usePatientProfile();
  const [localPreferences, setLocalPreferences] = useState<PatientPreferencesResponse>(patientPreferences);
  const contactApi = useMemo(() => new ContactApi(config, oidcService), [config, oidcService]);

  const updateLocalPreferences = (state: Partial<PatientPreferencesResponse>) => {
    const newState = { ...localPreferences, ...state };
    setLocalPreferences(newState);
  };

  const updateTextPreferences = async () => {
    await contactApi.patchPatientPreferences(localPreferences.patientId, localPreferences as PatientPreferencesRequest);
    await refreshPatientPreferences();
    showSuccess('Text message preferences successfully saved');
    goTo(WidgetRoutePath.COMM_PREFERENCES);
  };

  return (
    <>
      <PatientProfileHeader />
      <QuickNavMenu />
      <ViewContentWrapper>
        <PreferencesViewWrapper onSave={updateTextPreferences}>
          <AllCommunicationSection
            text="All Text Messages"
            isChecked={
              localPreferences.textAppointmentReminders &&
              localPreferences.textPatientReactivation &&
              localPreferences.textRecalls &&
              localPreferences.textReviewsAndSurveys &&
              localPreferences.textSpecialsAndNewsletters &&
              localPreferences.textTreatmentPlanFollowUps
            }
            onChange={(value) =>
              updateLocalPreferences({
                textAppointmentReminders: value,
                textPatientReactivation: value,
                textRecalls: value,
                textReviewsAndSurveys: value,
                textSpecialsAndNewsletters: value,
                textTreatmentPlanFollowUps: value
              })
            }
          />
          <CommunicationSubSectionHeader displayText="ESSENTIAL COMMUNICATION" />
          <CommunicationSubSection
            text="Appointment Reminders"
            isChecked={localPreferences.textAppointmentReminders}
            onChange={(value) => updateLocalPreferences({ textAppointmentReminders: value })}
          />
          <CommunicationSubSection
            text="Patient Reactivation"
            isChecked={localPreferences.textPatientReactivation}
            onChange={(value) => updateLocalPreferences({ textPatientReactivation: value })}
          />
          <CommunicationSubSection
            text="Recalls"
            isChecked={localPreferences.textRecalls}
            onChange={(value) => updateLocalPreferences({ textRecalls: value })}
          />
          <CommunicationSubSectionHeader displayText="MARKETING AND PROMOTIONS" />
          <AllPromotionalSubSection
            text="All Promotional Text Messages"
            isChecked={
              localPreferences.textTreatmentPlanFollowUps &&
              localPreferences.textReviewsAndSurveys &&
              localPreferences.textSpecialsAndNewsletters
            }
            onChange={(value) =>
              updateLocalPreferences({
                textTreatmentPlanFollowUps: value,
                textReviewsAndSurveys: value,
                textSpecialsAndNewsletters: value
              })
            }
          />
          <CommunicationSubSection
            isPromotionalComSubSection={true}
            text="Treatment Plan Follow-ups"
            isChecked={localPreferences.textTreatmentPlanFollowUps}
            onChange={(value) => updateLocalPreferences({ textTreatmentPlanFollowUps: value })}
          />
          <CommunicationSubSection
            isPromotionalComSubSection={true}
            text="Reviews & Surveys"
            isChecked={localPreferences.textReviewsAndSurveys}
            onChange={(value) => updateLocalPreferences({ textReviewsAndSurveys: value })}
          />
          <CommunicationSubSection
            isPromotionalComSubSection={true}
            text="Specials & Newsletters"
            isChecked={localPreferences.textSpecialsAndNewsletters}
            onChange={(value) => updateLocalPreferences({ textSpecialsAndNewsletters: value })}
          />
        </PreferencesViewWrapper>
      </ViewContentWrapper>
    </>
  );
};
